import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const RevenueAnalyticsChart = ({ dateRange, provider }) => {
  const revenueData = [
    { month: 'Jan', collections: 145000, procedures: 125000, insurance: 95000 },
    { month: 'Feb', collections: 158000, procedures: 138000, insurance: 102000 },
    { month: 'Mar', collections: 162000, procedures: 145000, insurance: 108000 },
    { month: 'Apr', collections: 171000, procedures: 152000, insurance: 115000 },
    { month: 'May', collections: 168000, procedures: 148000, insurance: 112000 },
    { month: 'Jun', collections: 185000, procedures: 165000, insurance: 125000 }
  ];

  const financialMetrics = [
    {
      label: 'Total Collections',
      value: '$989K',
      change: '+14.2%',
      trend: 'up',
      icon: 'DollarSign',
      color: 'bg-success/10 text-success',
      benchmark: '$950K Target'
    },
    {
      label: 'Procedure Revenue',
      value: '$873K',
      change: '+12.8%',
      trend: 'up',
      icon: 'TrendingUp',
      color: 'bg-primary/10 text-primary',
      benchmark: '$850K Target'
    },
    {
      label: 'Insurance Collections',
      value: '$657K',
      change: '+9.5%',
      trend: 'up',
      icon: 'FileText',
      color: 'bg-warning/10 text-warning',
      benchmark: '$625K Target'
    },
    {
      label: 'Avg Revenue/Patient',
      value: '$2,845',
      change: '+8.3%',
      trend: 'up',
      icon: 'Users',
      color: 'bg-muted text-muted-foreground',
      benchmark: '$2,700 Target'
    }
  ];

  const payerMix = [
    { name: 'Private Insurance', percentage: 45, amount: '$443K' },
    { name: 'Medicare/Medicaid', percentage: 28, amount: '$277K' },
    { name: 'Self-Pay', percentage: 18, amount: '$178K' },
    { name: 'Other', percentage: 9, amount: '$91K' }
  ];

  return (
    <div className="bg-card rounded-xl border border-border shadow-elevation-2 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg md:text-xl font-semibold text-foreground flex items-center gap-2">
          <Icon name="DollarSign" size={20} className="text-primary" />
          Revenue Analytics
        </h2>
        <span className="text-xs text-muted-foreground">
          {dateRange === 'day' ? 'Today' : dateRange === 'week' ? 'This Week' : dateRange === 'month' ? 'This Month' : dateRange === 'quarter' ? 'This Quarter' : 'This Year'}
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {financialMetrics?.map((metric, index) => (
          <div key={index} className="bg-muted/50 rounded-lg p-4 border border-border">
            <div className="flex items-center justify-between mb-3">
              <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${metric?.color}`}>
                <Icon name={metric?.icon} size={20} />
              </div>
              <div className="flex items-center gap-1">
                <Icon
                  name={metric?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'}
                  size={14}
                  className="text-success"
                />
                <span className="text-xs font-semibold text-success">{metric?.change}</span>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-1">{metric?.label}</p>
            <p className="text-2xl font-bold text-foreground mb-2">{metric?.value}</p>
            <div className="pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">{metric?.benchmark}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <h3 className="text-sm font-semibold text-foreground mb-4">Revenue Trends</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorCollections" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#059669" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorProcedures" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2563EB" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#2563EB" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="colorInsurance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#D97706" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#D97706" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis
                dataKey="month"
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="var(--color-muted-foreground)"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value / 1000}K`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--color-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px',
                  color: 'var(--color-foreground)'
                }}
                formatter={(value) => `$${value?.toLocaleString()}`}
              />
              <Legend
                wrapperStyle={{
                  fontSize: '12px',
                  color: 'var(--color-foreground)'
                }}
              />
              <Area
                type="monotone"
                dataKey="collections"
                stroke="#059669"
                fillOpacity={1}
                fill="url(#colorCollections)"
                strokeWidth={2}
                name="Collections"
              />
              <Area
                type="monotone"
                dataKey="procedures"
                stroke="#2563EB"
                fillOpacity={1}
                fill="url(#colorProcedures)"
                strokeWidth={2}
                name="Procedures"
              />
              <Area
                type="monotone"
                dataKey="insurance"
                stroke="#D97706"
                fillOpacity={1}
                fill="url(#colorInsurance)"
                strokeWidth={2}
                name="Insurance"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="pt-6 border-t border-border">
        <h3 className="text-sm font-semibold text-foreground mb-4">Payer Mix Distribution</h3>
        <div className="space-y-3">
          {payerMix?.map((payer, index) => (
            <div key={index} className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-foreground">{payer?.name}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-foreground">{payer?.amount}</span>
                    <span className="text-xs text-muted-foreground">({payer?.percentage}%)</span>
                  </div>
                </div>
                <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: `${payer?.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalyticsChart;